// hooks/useBuilder.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BuilderField, FieldTypeCode } from '../types/BuilderField';
import { FormBuilderAdapter } from '../types/BuilderAdapter';
import { groupFields } from '../utils/groupFields';

export interface UseBuilderOptions {
  adapter: FormBuilderAdapter;
  defaultCategories?: string[];
  onChange?: (fields: BuilderField[]) => void;
}

/**
 * All builder state and orchestration lives here. The builder never talks to
 * a backend directly — every mutation goes through `adapter`.
 */
export function useBuilder({ adapter, defaultCategories = [], onChange }: UseBuilderOptions) {
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return adapter
      .loadFields()
      .then((loaded) => setFields(loaded))
      .catch((e) => setError(e?.message ?? 'Failed to load fields.'))
      .finally(() => setLoading(false));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChange?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const categories = useMemo(() => {
    const fromFields = Array.from(new Set(fields.map((f) => f.category).filter(Boolean)));
    const merged = Array.from(new Set([...(defaultCategories ?? []), ...fromFields]));
    return merged;
  }, [fields, defaultCategories]);

  const sections = useMemo(() => groupFields(fields), [fields]);

  const saveField = useCallback(
    async (field: BuilderField, isNew: boolean) => {
      setSaving(true);
      setError(null);
      try {
        const saved = isNew ? await adapter.createField(field) : await adapter.updateField(field);
        setFields((prev) => {
          const exists = prev.some((f) => f.id === saved.id);
          return exists ? prev.map((f) => (f.id === saved.id ? saved : f)) : [...prev, saved];
        });
        return saved;
      } catch (e: any) {
        setError(e?.message ?? 'Failed to save field.');
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [adapter]
  );

  const deleteField = useCallback(
    async (field: BuilderField) => {
      setSaving(true);
      setError(null);
      try {
        await adapter.deleteField(field.id);
        setFields((prev) => prev.filter((f) => f.id !== field.id));
      } catch (e: any) {
        setError(e?.message ?? 'Failed to delete field.');
      } finally {
        setSaving(false);
      }
    },
    [adapter]
  );

  const moveField = useCallback(
    (field: BuilderField, dir: -1 | 1) => {
      const categoryFields = fields
        .filter((f) => f.category === field.category)
        .sort((a, b) => {
          if (a.orderNo !== b.orderNo) return a.orderNo - b.orderNo;
          return String(a.id).localeCompare(String(b.id));
        });

      const currentIndex = categoryFields.findIndex((f) => f.id === field.id);
      const targetIndex = currentIndex + dir;
      if (targetIndex < 0 || targetIndex >= categoryFields.length) return;

      const targetField = categoryFields[targetIndex];
      const updated = fields.map((f) => {
        if (f.id === field.id) return { ...f, orderNo: targetField.orderNo };
        if (f.id === targetField.id) return { ...f, orderNo: field.orderNo };
        return f;
      });

      setFields(updated);
      if (adapter.reorderFields) {
        adapter.reorderFields(updated).catch((e) => setError(e?.message ?? 'Failed to save new order.'));
      }
    },
    [fields, adapter]
  );

  return {
    fields,
    sections,
    categories,
    loading,
    saving,
    error,
    setError,
    reload: load,
    saveField,
    deleteField,
    moveField,
  };
}